import { getNote } from '@/lib/music/notes';
import { isOnLine } from '@/lib/music/staff';

export interface NoteStat {
  correct: number;
  wrong: number;
}

export type NoteStatsMap = Record<string, NoteStat>;

export interface ScoreState {
  correct: number;
  wrong: number;
  streak: number;
  bestStreak: number;
  total: number;
}

export type ExerciseKind = 'note' | 'line-space';

export interface NoteExercise {
  kind: 'note';
  /** resposta correta */
  noteId: string;
  /** dica opcional em destaque, ex: destacar o Dó central no teclado */
  emphasizedMidi?: number;
}

export interface LineSpaceExercise {
  kind: 'line-space';
  noteId: string;
  answer: 'line' | 'space';
}

export type Exercise = NoteExercise | LineSpaceExercise;

export function createScore(): ScoreState {
  return { correct: 0, wrong: 0, streak: 0, bestStreak: 0, total: 0 };
}

export function applyResult(score: ScoreState, correct: boolean): ScoreState {
  const streak = correct ? score.streak + 1 : 0;
  return {
    correct: score.correct + (correct ? 1 : 0),
    wrong: score.wrong + (correct ? 0 : 1),
    streak,
    bestStreak: Math.max(score.bestStreak, streak),
    total: score.total + 1,
  };
}

export function accuracy(score: ScoreState): number {
  if (score.total === 0) return 0;
  return Math.round((score.correct / score.total) * 100);
}

export function randomFrom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/** Embaralha uma lista sem alterar a original (algoritmo de Fisher-Yates). */
export function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Escolhe a próxima nota de forma adaptativa:
 * - notas com mais erros ganham peso;
 * - notas já dominadas perdem peso;
 * - notas muito recentes quase não se repetem.
 */
export function pickNextNoteId(
  pool: string[],
  stats: NoteStatsMap,
  recent: string[],
): string {
  if (pool.length === 0) throw new Error('Pool de notas vazio');
  if (pool.length === 1) return pool[0];

  const weights = pool.map((id) => {
    const stat = stats[id] ?? { correct: 0, wrong: 0 };
    let weight = 1 + stat.wrong * 2.5 - Math.min(stat.correct, 6) * 0.35;
    if (recent.includes(id)) weight *= 0.12;
    return Math.max(weight, 0.06);
  });

  const total = weights.reduce((sum, w) => sum + w, 0);
  let target = Math.random() * total;
  for (let i = 0; i < pool.length; i += 1) {
    target -= weights[i];
    if (target <= 0) return pool[i];
  }
  return pool[pool.length - 1];
}

export function pushRecent(recent: string[], id: string, max = 2): string[] {
  return [id, ...recent].slice(0, max);
}

export function buildNoteExercise(
  pool: string[],
  stats: NoteStatsMap,
  recent: string[],
  emphasizeMiddleC = false,
): NoteExercise {
  const noteId = pickNextNoteId(pool, stats, recent);
  const note = getNote(noteId);
  return {
    kind: 'note',
    noteId,
    emphasizedMidi: emphasizeMiddleC ? note.midi : undefined,
  };
}

export function buildLineSpaceExercise(
  pool: string[],
  stats: NoteStatsMap,
  recent: string[],
): LineSpaceExercise {
  const noteId = pickNextNoteId(pool, stats, recent);
  const note = getNote(noteId);
  return {
    kind: 'line-space',
    noteId,
    answer: isOnLine(note.step) ? 'line' : 'space',
  };
}

export function registerNoteResult(
  stats: NoteStatsMap,
  noteId: string,
  correct: boolean,
): NoteStatsMap {
  const current = stats[noteId] ?? { correct: 0, wrong: 0 };
  return {
    ...stats,
    [noteId]: {
      correct: current.correct + (correct ? 1 : 0),
      wrong: current.wrong + (correct ? 0 : 1),
    },
  };
}

/** Notas que o aluno mais erra, usadas para mensagens de reforço. */
export function weakestNotes(stats: NoteStatsMap, min = 1): string[] {
  return Object.entries(stats)
    .filter(([, stat]) => stat.wrong >= min && stat.wrong > stat.correct)
    .map(([id]) => id);
}